/**
 * Recipe showcase — overlay primitives (menus, dialog, toggle, slider).
 *
 * Enumerates every variant × state of each CVA recipe so the Tahoe restyle can
 * be judged in the real token environment. Imports ONLY from the shared UI
 * barrel and @/lib/cn (workshop rules). Dummy data + local state only.
 */

import { useState } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  ContextMenu,
  ContextMenuTrigger,
  ConfirmDialog,
  ToggleSwitch,
  Slider,
  Icons,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import { menuSurfaceVariants } from "@/components/ui/dropdown-menu";
import { confirmDialogPanelVariants } from "@/components/ui/confirm-dialog";
import { cn } from "@/lib/cn";
import { Section, Row, Label, StaticMenuItem } from "./recipes-primitives";

/* ─── DropdownMenu ────────────────────────────────────────────────────────── */

export function DropdownMenuSection() {
  return (
    <Section
      name="dropdown-menu"
      title="DropdownMenu"
      caption="menu surface · 24px radius · 8px padding · items 16px radius · min-h appkit-xl · body text"
    >
      <div className="flex flex-wrap items-start gap-10">
        <div>
          <Label>static open (always visible)</Label>
          {/* Rendered inline with the shared recipe so the surface is visible
              without interaction. Mirrors DropdownMenuContent styling. */}
          <div
            className={cn(
              surfaceRoleClassName("menu"),
              menuSurfaceVariants(),
              "w-56",
            )}
            {...surfaceRoleAttributes("menu", {
              nativeLevel: "root",
              nativeGroup: "menus",
            })}
            data-testid="recipe-dropdown-static"
          >
            <StaticMenuItem icon={<Icons.Edit className="h-4 w-4" />}>
              Rename
            </StaticMenuItem>
            <StaticMenuItem icon={<Icons.Copy className="h-4 w-4" />}>
              Duplicate
            </StaticMenuItem>
            <div className="mx-0 my-appkit-2xs h-px bg-glass-stroke-subtle" />
            <StaticMenuItem
              icon={<Icons.Trash2 className="h-4 w-4" />}
              destructive
            >
              Delete
            </StaticMenuItem>
          </div>
        </div>

        <div>
          <Label>interactive trigger</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" data-testid="recipe-dropdown-trigger">
                Open menu
                <Icons.ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Rename</DropdownMenuItem>
              <DropdownMenuItem>Duplicate</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Section>
  );
}

/* ─── ContextMenu ─────────────────────────────────────────────────────────── */

export function ContextMenuSection() {
  return (
    <Section
      name="context-menu"
      title="ContextMenu"
      caption="shared menu surface · same recipe as DropdownMenu · danger item in danger-light"
    >
      <div className="flex flex-wrap items-start gap-10">
        <div>
          <Label>static open (always visible)</Label>
          {/* ContextMenu only opens on right-click; render the shared surface
              inline so the open state is visible for review. */}
          <div
            className={cn(
              surfaceRoleClassName("menu"),
              menuSurfaceVariants(),
              "w-48",
            )}
            {...surfaceRoleAttributes("menu", {
              nativeLevel: "root",
              nativeGroup: "menus",
            })}
            role="menu"
            data-testid="recipe-context-static"
          >
            <StaticMenuItem icon={<Icons.Edit className="h-4 w-4" />}>
              Edit
            </StaticMenuItem>
            <StaticMenuItem icon={<Icons.Copy className="h-4 w-4" />}>
              Copy
            </StaticMenuItem>
            <div className="my-appkit-2xs h-px bg-glass-stroke-subtle" />
            <StaticMenuItem
              icon={<Icons.Trash2 className="h-4 w-4" />}
              destructive
            >
              Delete
            </StaticMenuItem>
          </div>
        </div>

        <div>
          <Label>interactive (right-click target)</Label>
          <ContextMenu>
            <ContextMenuTrigger>
              <div
                className="flex h-24 w-48 items-center justify-center rounded-panel border border-dashed border-glass-stroke text-appkit-caption text-muted"
                data-testid="recipe-context-trigger"
              >
                Right-click here
              </div>
            </ContextMenuTrigger>
          </ContextMenu>
        </div>
      </div>
    </Section>
  );
}

/* ─── ConfirmDialog ───────────────────────────────────────────────────────── */

export function ConfirmDialogSection() {
  const [variant, setVariant] = useState<"danger" | "default">("danger");
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <Section
      name="confirm-dialog"
      title="ConfirmDialog"
      caption="menu surface · z31800 overlay · w320 · danger / default confirm button"
    >
      <Row>
        <Button
          variant={variant === "danger" ? "default" : "secondary"}
          size="sm"
          onClick={() => setVariant("danger")}
          data-testid="recipe-confirm-variant-danger"
        >
          danger
        </Button>
        <Button
          variant={variant === "default" ? "default" : "secondary"}
          size="sm"
          onClick={() => setVariant("default")}
          data-testid="recipe-confirm-variant-default"
        >
          default
        </Button>
      </Row>
      {/* Static panel replica — the real dialog portals a fixed full-screen
          overlay that would block scrolling/clicking on the whole page. */}
      <div
        className={cn(
          surfaceRoleClassName("menu"),
          confirmDialogPanelVariants(),
        )}
        data-testid="recipe-confirm-dialog"
        {...surfaceRoleAttributes("menu", {
          nativeLevel: "root",
          nativeGroup: "menus",
        })}
      >
        <div className="flex items-center gap-appkit-xs border-b border-glass-stroke-faint px-[20px] py-appkit-sm">
          <span className="text-appkit-body font-semibold text-primary">
            {variant === "danger" ? "Delete frame?" : "Confirm action"}
          </span>
        </div>
        <div className="px-[20px] py-appkit-sm">
          <p className="text-appkit-body text-secondary">
            {variant === "danger"
              ? "This permanently removes the frame and its history. This cannot be undone."
              : "Apply these changes to the current workflow?"}
          </p>
        </div>
        <div className="flex items-center justify-end gap-appkit-xs border-t border-glass-stroke-faint px-[20px] py-appkit-sm">
          <Button
            type="button"
            variant="secondary"
            data-testid="recipe-confirm-static-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={variant === "danger" ? "destructive" : "default"}
            data-testid="recipe-confirm-static-confirm"
          >
            {variant === "danger" ? "Delete" : "Apply"}
          </Button>
        </div>
      </div>
      <Row>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setDialogOpen(true)}
          data-testid="recipe-confirm-open-real"
        >
          Open real dialog (with overlay)
        </Button>
      </Row>
      {dialogOpen && (
        <ConfirmDialog
          title={variant === "danger" ? "Delete frame?" : "Confirm action"}
          description={
            variant === "danger"
              ? "This permanently removes the frame and its history. This cannot be undone."
              : "Apply these changes to the current workflow?"
          }
          confirmLabel={variant === "danger" ? "Delete" : "Apply"}
          variant={variant}
          onConfirm={() => setDialogOpen(false)}
          onCancel={() => setDialogOpen(false)}
          data-testid="recipe-confirm-dialog-real"
        />
      )}
    </Section>
  );
}

/* ─── ToggleSwitch ────────────────────────────────────────────────────────── */

export function ToggleSwitchSection() {
  const [off, setOff] = useState(false);
  const [on, setOn] = useState(true);

  return (
    <Section
      name="toggle-switch"
      title="ToggleSwitch"
      caption="44×26 pill track · 22px white knob · OFF heavy glass + stroke · ON accent + glow"
    >
      <Row>
        <div className="flex items-center gap-2">
          <ToggleSwitch
            checked={off}
            onCheckedChange={setOff}
            data-testid="recipe-toggle-off"
          />
          <span className="text-appkit-caption text-muted">off</span>
        </div>
        <div className="flex items-center gap-2">
          <ToggleSwitch
            checked={on}
            onCheckedChange={setOn}
            data-testid="recipe-toggle-on"
          />
          <span className="text-appkit-caption text-muted">on</span>
        </div>
        <div className="flex items-center gap-2">
          <ToggleSwitch
            checked={true}
            onCheckedChange={() => {}}
            disabled
            data-testid="recipe-toggle-disabled"
          />
          <span className="text-appkit-caption text-muted">disabled</span>
        </div>
      </Row>
    </Section>
  );
}

/* ─── Slider ──────────────────────────────────────────────────────────────── */

export function SliderSection() {
  const [value, setValue] = useState(60);

  return (
    <Section
      name="slider"
      title="Slider"
      caption="5px pill track · dense glass + stroke · 20px white thumb (not accent)"
    >
      <div className="flex max-w-sm flex-col gap-5">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <Label>interactive</Label>
            <span
              className="text-appkit-caption text-secondary"
              data-testid="recipe-slider-value"
            >
              {value}
            </span>
          </div>
          <Slider
            value={value}
            onValueChange={setValue}
            min={0}
            max={100}
            data-testid="recipe-slider"
          />
        </div>
        <div>
          <Label>disabled</Label>
          <Slider
            value={40}
            onValueChange={() => {}}
            min={0}
            max={100}
            disabled
            data-testid="recipe-slider-disabled"
          />
        </div>
      </div>
    </Section>
  );
}
